/* Sad Boy Saga — free-shoe pre-checkout offer CHAIN.
 *
 * 2026-07-30 rebuild. This was a two-tab deal CHOOSER ("Buy 1 Get 1" | "Buy 2 Get 3")
 * where the shopper picked a tier and the popup trusted the pick. Derrick asked for a
 * chain instead: the tier is whatever the cart quantity has already earned, and the
 * popup names the next step up.
 *
 * THE ONE RULE IN THIS FILE: every number shown to a shopper is derived from the
 * MEASURED allocation ladder, never from a model of how BXGY "should" behave.
 * The 4-pair rung alone moved three times in two days — a model said 2 free, the
 * store granted 1; a new B2G2 rule moved it back to 2 with no deploy; retiring the
 * B2G3 rule flattened everything above it. None of that is visible from code.
 * Two guards enforce the rule:
 *
 *   1. The ladder comes from `shop.metafields.sbs.shoe_offer`, re-read from the
 *      server (`/cart?view=offer`) before any number is displayed. A cached document
 *      cannot promise a count the server will not grant.
 *   2. Above `measured_through` there IS no measured answer, so the copy goes
 *      numberless. It never extrapolates, and it never says "maxed" — the ladder
 *      is only known as far as it has been measured.
 *
 * Free-pair count actually granted right now is read from /cart.js as UNITS on
 * fully-zeroed lines, never lines, and never by matching a discount title (~18 other
 * automatic discounts are live and any of them can win a cart outright).
 *
 * Interop with sbs-app.js:
 *   window.sbsRenderCart(cart)  -> re-render the cart drawer after our add
 *   window.sbsToast(msg)        -> surface an error to the shopper
 *   'sbs:cart:updated' event    -> fires on every drawer render so we re-sync
 * This module OWNS [data-offer-bar] in the drawer; sbs-app.js must not write it.
 */
(function () {
  'use strict';
  var modal = document.querySelector('[data-fs-modal]');
  var overlay = document.querySelector('[data-fs-overlay]');
  if (!modal) return;

  function $(s, r) { return (r || modal).querySelector(s); }
  function $$(s, r) { return Array.prototype.slice.call((r || modal).querySelectorAll(s)); }
  // The picker exists on TWO surfaces now — inline in the cart drawer and inside the
  // pre-checkout modal (Derrick 2026-07-31: "the cart draw needs the multi select
  // option"). Anything that touches cards must reach both, or a pick made in the drawer
  // leaves the modal's copy of the same shoe showing an un-picked state.
  function allCards() { return $$('[data-fs-card]', document); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
  function money(cents) { var n = (Number(cents) || 0) / 100; return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)); }
  function plural(n, one, many) { return n === 1 ? one : (many || one + 's'); }
  function comesOff(n) { return n === 1 ? 'comes off' : 'come off'; }
  function withWidth(url, w) { if (!url) return ''; try { var u = new URL(url, window.location.origin); u.searchParams.set('width', w); return u.toString(); } catch (e) { return url; } }
  // Say something to the shopper, and actually mean it.
  //
  // `window.sbsToast` lives in sbs-app.js. The old one-liner was
  // `if (typeof window.sbsToast === 'function') window.sbsToast(m)` — so if that file
  // failed to load or threw before defining it, every error message this module has
  // was swallowed. An error path that itself fails silently is the worst version of
  // the bug we keep chasing: the shopper taps, nothing happens, nothing explains it.
  //
  // Fallback is the drawer CTA's own sub-label — markup this module owns, always on
  // screen whenever the offer is, and already styled.
  var sayTimer = null;
  function say(m) {
    if (!m) return;
    if (typeof window.sbsToast === 'function') { window.sbsToast(m); return; }
    try { console.warn('[sbs-offer] ' + m); } catch (e) {}
    var sub = document.querySelector('[data-fs-drawer-sub]');
    if (!sub) return;
    var prev = sub.textContent, wasHidden = sub.hidden;
    sub.textContent = m;
    sub.hidden = false;
    if (sayTimer) clearTimeout(sayTimer);
    sayTimer = setTimeout(function () {
      // Only restore if nothing else has written since — a render may have moved on.
      if (sub.textContent === m) { sub.textContent = prev; sub.hidden = wasHidden; }
    }, 4000);
  }

  /* ------------------------------------------------------------------ *
   * Eligibility manifest — which products the discount actually covers.
   * /cart.js carries no collection membership, so without this the cart
   * cannot be counted. A TRUNCATED manifest under-counts and under-promises,
   * so a length mismatch fails closed to numberless copy.
   * ------------------------------------------------------------------ */
  var SHOE_IDS = new Set();
  var MANIFEST_OK = false;
  (function () {
    var el = document.querySelector('[data-sbs-shoe-ids]');
    if (!el) return;
    try {
      (JSON.parse(el.textContent) || []).forEach(function (id) { SHOE_IDS.add(Number(id)); });
    } catch (e) { SHOE_IDS = new Set(); return; }
    var declared = Number(el.getAttribute('data-shoe-total'));
    MANIFEST_OK = SHOE_IDS.size > 0 && (!declared || SHOE_IDS.size === declared);
  })();

  /* ------------------------------------------------------------------ *
   * Offer config (the measured ladder).
   * ------------------------------------------------------------------ */
  var CFG = null;          // normalized ladder, or null
  var CFG_AUTHORITATIVE = false;  // true only after a server round-trip confirmed it
  var CFG_AT = 0;
  var CFG_TTL = 60000;
  var cfgInFlight = null;

  function normalizeCfg(raw) {
    if (!raw || typeof raw !== 'object' || !raw.rungs || typeof raw.rungs !== 'object') return null;
    var rungs = {};
    Object.keys(raw.rungs).forEach(function (k) {
      var n = Number(k), v = Number(raw.rungs[k]);
      if (isFinite(n) && n >= 1 && isFinite(v) && v >= 0) rungs[n] = v;
    });
    var keys = Object.keys(rungs).map(Number);
    if (!keys.length) return null;
    var mt = Number(raw.measured_through);
    // An unset/oversized measured_through must not license extrapolation: clamp it
    // to the largest rung actually present.
    var maxKey = Math.max.apply(null, keys);
    if (!isFinite(mt) || mt < 1 || mt > maxKey) mt = maxKey;
    return {
      rungs: rungs,
      measuredThrough: mt,
      configId: String(raw.config_id || ''),
      revision: Number(raw.revision) || 0
    };
  }

  // First-paint HINT from the drawer's embedded copy. Never displayed as a number —
  // it only lets the chain skip a frame of layout once the server confirms it.
  (function () {
    var el = document.querySelector('[data-sbs-offer-config]');
    if (!el) return;
    try { CFG = normalizeCfg(JSON.parse(el.textContent)); } catch (e) { CFG = null; }
  })();

  function loadCfg(force) {
    if (!force && CFG_AUTHORITATIVE && (Date.now() - CFG_AT) < CFG_TTL) return Promise.resolve(CFG);
    if (cfgInFlight) return cfgInFlight;
    // `Accept: text/html` is REQUIRED and was a real bug (2026-07-31, caught by Derrick:
    // "the upsell flow didn't happen when I hit checkout"). Measured against the live
    // storefront:
    //     Accept: text/plain        -> 404          <-- what this used to send
    //     Accept: text/html         -> 200, the offer config
    //     Accept: */*               -> 200, the offer config
    //     Accept: application/json  -> 200, but Shopify's CART json, NOT this template
    // Shopify content-negotiates page routes and refuses text/plain outright. The 404
    // was swallowed by the catch below, so the chain silently never armed: no config
    // means not `ready()`, and the checkout intercept deliberately stands down rather
    // than promise a free pair it cannot verify. Correct fail-closed behaviour hiding a
    // broken request — which is exactly why the catch now warns instead of going quiet.
    cfgInFlight = fetch('/cart?view=offer', { headers: { 'Accept': 'text/html' }, cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw new Error('offer config HTTP ' + r.status); return r.text(); })
      .then(function (t) {
        var next = normalizeCfg(JSON.parse(t.trim()));
        // A well-formed "no offer configured" answer is authoritative too — it means
        // render numberless, not "keep believing the stale hint".
        CFG = next;
        CFG_AUTHORITATIVE = true;
        CFG_AT = Date.now();
        return CFG;
      })
      .catch(function (err) {
        // Leave CFG_AUTHORITATIVE false so nothing numeric renders. A failed read is
        // not permission to fall back to the hint.
        //
        // But SAY SO. Failing closed is right; failing closed silently is how a broken
        // endpoint looked identical to "no offer running" for a whole build.
        try {
          console.warn('[sbs-offer] config unavailable — chain stays numberless and the '
            + 'checkout intercept is disabled. ' + (err && err.message ? err.message : err));
        } catch (e) {}
        return null;
      })
      .then(function (v) { cfgInFlight = null; return v; });
    return cfgInFlight;
  }

  /* ------------------------------------------------------------------ *
   * Ladder math. Every function returns null for "not measured" — callers
   * must treat null as "say nothing numeric", never as zero.
   * ------------------------------------------------------------------ */
  function ready() { return !!(CFG && CFG_AUTHORITATIVE && MANIFEST_OK); }

  function freeAt(n) {
    if (!ready() || n < 0 || n > CFG.measuredThrough) return null;
    if (n === 0) return 0;
    var v = CFG.rungs[n];
    return typeof v === 'number' ? v : null;
  }
  function paidAt(n) { var f = freeAt(n); return f === null ? null : n - f; }

  // How many pairs to offer RIGHT NOW: the jump that leaves the shopper paying the
  // LEAST, and never more than they already pay. Derived from the ladder, never
  // configured — at n=1 the ladder says paying for 1 buys 2 pairs, so one slot is
  // free; at n=2 the third pair is fully paid, so none.
  //
  // Only the ENDPOINT is tested, not every step along the way. Picks are staged and
  // committed in one multi-item add, so intermediate cart sizes never exist — a jump
  // of 2 can be free even when a jump of 1 costs more, and requiring each step to be
  // free would hide that offer entirely. `commitOk` is what stops a shopper stopping
  // halfway and paying for it.
  function freeSlots(n) {
    var base = paidAt(n);
    if (base === null) return 0;
    var best = 0, bestPaid = base;
    for (var k = 1; n + k <= CFG.measuredThrough; k++) {
      var p = paidAt(n + k);
      if (p === null) continue;                 // gap in the ladder — skip, don't stop
      if (p < bestPaid) { bestPaid = p; best = k; }
      else if (p === bestPaid && p <= base) { best = k; }  // same cost, more shoes
    }
    return best;
  }

  // Is committing exactly `k` staged picks safe? Anything that would leave the cart
  // costing MORE than it does now is refused: the shopper was told these are free.
  function commitOk(n, k) {
    if (k < 1) return false;
    var base = paidAt(n), p = paidAt(n + k);
    return base !== null && p !== null && p <= base;
  }

  // How many pairs the shopper may pick right now.
  //
  // Two different offers live here and they have different rules. A FREE-SLOT offer
  // adds pairs at no extra cost. A NEXT-RUNG offer ("get 2 more pairs, pay for the one
  // you just added") deliberately costs one more paid pair — the shopper is buying,
  // knowingly, because the pairs-per-dollar improves. Gating both on "must not cost
  // more" locked every card at 2 pairs, so the modal made an offer it then refused to
  // let anyone take.
  function pickLimit(n) {
    var slots = freeSlots(n);
    if (slots >= 1) return slots;
    var nu = nextUnlock(n);
    return nu ? nu.addPairs : 0;
  }

  // A free-slot pick may stop early only if stopping is still free.
  //
  // A next-rung pick used to be ALL-OR-NOTHING (`k === nu.addPairs`), and that was the
  // bug Derrick hit: "the extra things im adding for shoe pairs 2-4 dont work and arent
  // being added". Tapping a size staged the pick and `commit()` then refused it, so
  // nothing reached the cart and nothing said why — the pair simply vanished.
  //
  // The rule was over-cautious anyway. It existed to stop a shopper paying for half a
  // jump and getting nothing, but on this ladder every step pays for itself: from 2
  // pairs, buying the 3rd immediately makes the 4th FREE (paidAt(3)==paidAt(4)==2). So
  // a jump can be taken one pair at a time and the chain keeps its promise at every
  // stop. Anything BEYOND the rung is still refused — that would be buying at full
  // price with no offer behind it.
  function commitAllowed(n, k) {
    if (k < 1) return false;
    if (freeSlots(n) >= 1) return commitOk(n, k);
    var nu = nextUnlock(n);
    return !!nu && k <= nu.addPairs;
  }

  // The BEST next step up, not merely the first one.
  //
  // Measured 2026-07-31 with three shoe rules live: from a 2-pair cart, going to 4
  // costs 1 more paid pair and yields 1 more free; going to 5 costs the SAME 1 more
  // paid pair and yields 2 more free. Returning the first improvement offered the
  // 4 and quietly buried the strictly better 5 — the shopper pays identically either
  // way. So: cheapest first, and among equal cost, the most free pairs.
  function nextUnlock(n) {
    var cur = freeAt(n);
    if (cur === null) return null;
    var best = null;
    for (var m = n + 1; m <= CFG.measuredThrough; m++) {
      var f = freeAt(m);
      if (f === null || f <= cur) continue;
      var cand = { at: m, free: f, addPairs: m - n, addFree: f - cur,
                   addPaid: (m - f) - (n - cur) };
      if (!best || cand.addPaid < best.addPaid ||
          (cand.addPaid === best.addPaid && cand.addFree > best.addFree)) {
        best = cand;
      }
    }
    return best;
  }

  /* ------------------------------------------------------------------ *
   * Server truth — what the cart has ACTUALLY been granted.
   * Counts UNITS on fully-zeroed qualifying lines. Shopify splits a line when a
   * discount covers some of its units but not others, so a zeroed line is exactly
   * the free units. Deriving free-count from the discount TOTAL instead would let
   * an unrelated percentage-off discount masquerade as free pairs.
   * ------------------------------------------------------------------ */
  function grantedUnits(cart) {
    var n = 0;
    (cart && cart.items || []).forEach(function (li) {
      if (!SHOE_IDS.has(Number(li.product_id))) return;
      var orig = Number(li.original_line_price) || 0;
      if (orig > 0 && Number(li.final_line_price) === 0) n += li.quantity;
    });
    return n;
  }
  function shoeCount(cart) {
    var n = 0;
    (cart && cart.items || []).forEach(function (it) {
      if (SHOE_IDS.has(Number(it.product_id))) n += it.quantity;
    });
    return n;
  }
  function inCartPids(cart) {
    var s = new Set();
    (cart && cart.items || []).forEach(function (it) {
      if (SHOE_IDS.has(Number(it.product_id))) s.add(Number(it.product_id));
    });
    return s;
  }

  /* ------------------------------------------------------------------ *
   * Copy. One function, so the modal / drawer bar / nudge can never
   * disagree about what the offer currently is.
   * ------------------------------------------------------------------ */
  var NEUTRAL = {
    eyebrow: 'Your offer',
    title: 'Before you check out.',
    sub: 'Free pairs unlock automatically — your lowest-priced pairs come off at checkout.',
    now: '', next: '', bar: 'Free pairs unlock at checkout — your lowest-priced pairs come off automatically.',
    slots: 0, progress: 0
  };

  function offerCopy(n, granted) {
    if (!ready() || n < 1) return NEUTRAL;
    var slots = freeSlots(n);
    var nu = nextUnlock(n);
    var paidNow = n - granted;
    var c = {
      eyebrow: granted > 0 ? granted + ' ' + plural(granted, 'pair') + ' free' : 'Your offer',
      slots: slots,
      now: n + ' ' + plural(n, 'pair') + (granted > 0 ? ' · ' + granted + ' free' : ''),
      next: '', progress: 0
    };

    if (slots >= 1) {
      var paidThen = paidAt(n + slots);
      c.next = '+' + slots + ' free at ' + (n + slots) + ' ' + plural(n + slots, 'pair');
      c.progress = Math.round((n / (n + slots)) * 100);
      if (paidThen !== null && paidThen < paidNow) {
        // Adding pairs makes the order CHEAPER — the ladder re-allocates so a pair
        // already paid for comes off. Worth saying plainly; it reads as a mistake
        // otherwise.
        c.title = 'Add ' + slots + ' more and your total goes down.';
        c.sub = 'You’re paying for ' + paidNow + ' ' + plural(paidNow, 'pair') + ' right now. At '
              + (n + slots) + ' pairs you pay for ' + paidThen + ' — the rest come off automatically at checkout.';
        c.bar = 'Add ' + slots + ' more ' + plural(slots, 'pair') + ' and your total drops.';
      } else {
        // n===1 gets Derrick's exact framing ("add a 2nd free" as the main offer);
        // every other rung states the count plainly.
        c.title = (n === 1 && slots === 1)
          ? 'Add a 2nd pair — it’s free.'
          : 'Add ' + slots + ' more ' + plural(slots, 'pair') + ' — ' + plural(slots, 'it’s', 'they’re') + ' free.';
        c.sub = 'You’re paying for ' + paidNow + ' ' + plural(paidNow, 'pair')
              + '. Adding ' + (slots === 1 ? 'one more' : slots + ' more') + ' costs nothing — your lowest-priced '
              + plural(slots, 'pair') + ' ' + comesOff(slots) + ' automatically at checkout.';
        c.bar = slots === 1 ? 'Add 1 more pair — it’s free.'
                            : 'Add ' + slots + ' more pairs — they’re free.';
      }
      return c;
    }

    // `addPaid` of 0 would mean the jump is free, and freeSlots would already have
    // taken it. Guarding anyway so a hand-edited ladder can never print "you only
    // pay for 0 of them".
    if (nu && nu.addPaid >= 1) {
      c.next = '+' + nu.addFree + ' free at ' + nu.at + ' pairs';
      c.progress = Math.round((n / nu.at) * 100);

      // Derrick's framing (2026-07-31): "get 2 more pairs free when you buy the one you
      // just added". It bills IDENTICALLY to the old wording — from 2 pairs you pay for
      // 1, from 4 you pay for 2 — because BXGY zeroes the lowest-priced units regardless
      // of which ones the shopper thinks they bought. Saying "add 2, one of them free"
      // and "pay for the one you just added, the next 2 are free" describe the same
      // receipt; the second is the one people act on.
      //
      // The numbers are still derived, never written: addPairs is how many pairs the
      // jump adds, addPaid is how many more pairs get charged. Only the sentence differs.
      if (nu.addPaid === 1) {
        c.title = 'Get ' + nu.addPairs + ' more ' + plural(nu.addPairs, 'pair')
                + ' free — just pay for the one you added.';
      } else {
        c.title = 'Get ' + nu.addPairs + ' more ' + plural(nu.addPairs, 'pair')
                + ' — you only pay for ' + nu.addPaid + '.';
      }
      c.sub = 'At ' + nu.at + ' pairs you pay for ' + (nu.at - nu.free) + ' and '
            + nu.free + ' come off automatically at checkout.';
      c.bar = 'Get ' + nu.addPairs + ' more ' + plural(nu.addPairs, 'pair') + ' for the price of '
            + (nu.addPaid === 1 ? 'one' : nu.addPaid) + '.';
      return c;
    }

    // Measured ladder has nothing further to say about this cart size. Report what
    // IS true and stop. No "maxed" — the ladder is only known as far as it has been
    // measured, and claiming a ceiling that has not been measured is a false promise.
    c.progress = granted > 0 ? 100 : 0;
    if (granted > 0) {
      c.title = granted + ' ' + plural(granted, 'pair') + ' free in your cart.';
      c.sub = 'Your lowest-priced ' + plural(granted, 'pair') + ' ' + comesOff(granted) + ' automatically at checkout.';
      c.bar = granted + ' ' + plural(granted, 'pair') + ' free — applied at checkout.';
    } else {
      c.title = NEUTRAL.title; c.sub = NEUTRAL.sub; c.bar = NEUTRAL.bar;
    }
    return c;
  }

  /* ------------------------------------------------------------------ *
   * Staged picks. Selections live here, NOT in the cart, until committed
   * in one multi-item add.
   * ------------------------------------------------------------------ */
  var staged = [];     // [{vid, pid, title, size}]
  var busy = false;
  var lastCart = null;

  function stagedFor(pid) { return staged.filter(function (s) { return s.pid === pid; }).length; }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        // Carry the status. `new Error('cart')` told whoever caught it nothing at all,
        // and this read genuinely does fail in the wild — Shopify rate-limits /cart*
        // per IP, so a burst of adds can get a 429 on the very next read.
        if (!r.ok) throw new Error('cart read failed (HTTP ' + r.status + ')');
        return r.json();
      });
  }

  /* ------------------------------------------------------------------ *
   * Render
   * ------------------------------------------------------------------ */
  function render(cart) {
    lastCart = cart;
    var count = shoeCount(cart);
    var granted = grantedUnits(cart);
    var c = offerCopy(count, granted);

    renderBar(c);
    // Order matters: the picker's visibility decides whether the nudge has anything
    // left to say, so it has to be settled first or the nudge flashes on the first
    // paint and hides on the second.
    renderDrawerCta(c, count);
    renderNudge(c, count);

    if (count < 1) {
      staged = [];
      if (modal.classList.contains('is-open')) closeModal();
      return;
    }

    // Declared BEFORE its first use. It previously sat further down with `var`, so
    // hoisting handed the chain block `undefined` — `chain.hidden = !c.now || picking`
    // evaluated to undefined whenever c.now was set, and the strip never hid at all.
    // Caught by the flow harness, not by reading the code.
    var picking = staged.length > 0;

    $('[data-fs-eyebrow]').textContent = c.eyebrow;
    $('[data-fs-title]').textContent = c.title;
    $('[data-fs-sub]').textContent = c.sub;

    var chain = $('[data-fs-chain]');
    var nextStep = $('[data-fs-next-step]');
    if (chain) {
      // Hidden while picking — the commit bar reports progress, and two status blocks
      // stacked above the cards is what made this area unreadable.
      chain.hidden = !c.now || picking;
      $('[data-fs-now]').textContent = c.now;
      // NEXT STEP stays hidden (Derrick 2026-07-31): previewing the next rung beside
      // the current offer put two asks on screen at once. The chain escalates on its
      // own — take the free pair and the headline becomes the next offer. The value is
      // still written so the drawer's offer bar and this modal never disagree.
      if (nextStep) {
        nextStep.hidden = true;
        $('[data-fs-next]').textContent = c.next;
      }
    }

    // Progress pips scale to the free slots on offer; with none, the strip carries
    // the state instead and pips would be a bar with nothing to fill.
    var limit = pickLimit(count);

    // DECLUTTER (Derrick 2026-07-31: "too much stuff in that area hard to tell whats
    // going on"). Four elements were reporting the same state at once — the eyebrow,
    // the "in your cart" strip, a pip counter, and the commit bar. While the shopper
    // is picking, the commit bar IS the status, so the other two step aside and the
    // pip row is gone entirely: a progress bar that duplicates the button's own label
    // is decoration, not information.
    var prog = $('[data-fs-prog]');
    if (prog) { prog.innerHTML = ''; prog.hidden = true; }

    $('[data-fs-thumbs]').innerHTML = (cart.items || []).slice(0, 6).map(function (it) {
      return '<img src="' + esc(withWidth(it.image, 80)) + '" alt="" loading="lazy">';
    }).join('');
    $('[data-fs-count]').textContent = cart.item_count + ' item' + (cart.item_count === 1 ? '' : 's');
    $('[data-fs-snapsub]').textContent = money(cart.total_price);
    var saved = cart.total_discount || 0;
    var saverow = $('[data-fs-saverow]');
    if (saved > 0) { saverow.hidden = false; $('[data-fs-saveval]').textContent = '-' + money(saved); }
    else saverow.hidden = true;
    $('[data-fs-subtotal]').textContent = money(cart.total_price);

    // Cards. A product already in the cart is hidden (a duplicate pick is almost
    // always a misclick). Locked when every free slot is spoken for.
    var have = inCartPids(cart);
    var full = staged.length >= limit;
    var freeNow = c.slots > 0;   // is the NEXT pick genuinely free, or part of a paid jump?
    allCards().forEach(function (card) {
      var pid = Number(card.getAttribute('data-pid'));
      card.hidden = have.has(pid);
      var picked = stagedFor(pid) > 0;
      card.classList.toggle('fs-card--staged', picked);
      card.classList.toggle('fs-card--locked', limit < 1 || (full && !picked));
      // Green CTA only when the pick is genuinely free; a paid jump keeps the neutral
      // dark button so the colour never implies free.
      card.classList.toggle('fs-card--free', freeNow && !picked);
      var price = card.querySelector('[data-fs-price]');
      if (price) {
        // Only strike the price and say FREE when this pick genuinely costs nothing.
        // During a paid jump the pairs are a better deal, not free, and the card must
        // not claim otherwise.
        var list = esc(price.getAttribute('data-list') || '');
        price.innerHTML = freeNow
          ? '<s>' + list + '</s> <span class="fs-free">Free</span>'
          : list;
      }
      var addBtn = card.querySelector('[data-fs-add]');
      if (addBtn) {
        addBtn.hidden = false;
        addBtn.textContent = picked ? 'Picked ✓' : (freeNow ? 'Add free' : 'Add this pair');
      }
      var sizes = card.querySelector('[data-fs-sizes]');
      if (sizes && !picked) sizes.hidden = true;
    });

    // Nothing left to pick — take the rail down rather than leave a wall of dimmed,
    // untappable cards under a headline that already said the offer is done. The popup
    // is still useful in that state: the title reports what the cart earned and the
    // footer's Secure checkout is the one thing left to do.
    // (`.fs-rail` is `display:grid`, which out-specifies the UA's `[hidden]` rule — the
    // matching `.fs-rail[hidden]` lives in sbs-freeshoe.css. Same trap as .fs-modal__prog.)
    var railEl = $('[data-fs-rail]');
    if (railEl) {
      railEl.hidden = limit < 1 || !allCards().some(function (cd) { return !cd.hidden; });
    }

    renderClaim(c, count);
  }

  // Is there anything to offer this cart at all? One predicate, used by every surface —
  // the drawer picker, the drawer button, and the checkout intercept. They must agree:
  // a button that appears without its picker, or an interception with nothing behind
  // it, are both worse than showing nothing.
  function hasOffer(n) {
    return ready() && n >= 1 && (freeSlots(n) >= 1 || !!nextUnlock(n));
  }

  // How many shoes the offer can actually put in front of the shopper. A card is hidden
  // once its product is already in the cart, so someone holding one of each would get a
  // green button that opens a popup with nothing in it — a control promising a gift it
  // cannot deliver. Counted from the CART, not the DOM, because this runs before the card
  // loop repaints.
  function pickableCount(cart) {
    var have = inCartPids(cart);
    return allCards().filter(function (cd) {
      return !have.has(Number(cd.getAttribute('data-pid')));
    }).length;
  }

  // What the primary control DOES right now, independent of which surface draws it.
  // Both the drawer button and the modal button render from this, so they can never
  // disagree about whether a jump is committable or how many picks are still missing.
  // `kind` is the behaviour; the labels for the idle state are the surface's own,
  // because the drawer is making an offer and the modal is showing the way out.
  function offerAction(c, n) {
    var limit = pickLimit(n);
    var short = limit - staged.length;
    if (busy) return { kind: 'busy', main: 'Adding…', sub: '', disabled: true };
    if (staged.length && commitAllowed(n, staged.length)) {
      return {
        kind: 'confirm',
        main: 'Add ' + staged.length + ' ' + plural(staged.length, 'pair') + ' →',
        sub: c.slots > 0 ? 'No extra charge'
                         : 'You pay for 1 more — ' + staged.length + ' pairs are added',
        disabled: false
      };
    }
    if (staged.length) {
      return {
        kind: 'short',
        main: 'Pick ' + short + ' more ' + plural(short, 'pair'),
        sub: staged.length + ' of ' + limit + ' picked — the deal applies at ' + (n + limit) + ' pairs',
        disabled: true
      };
    }
    return { kind: 'idle', main: '', sub: '', disabled: false };
  }

  // The modal's footer button. Inside the modal the CARDS are the offer, so this
  // control is deliberately NOT a second pitch: mid-pick it is the CONFIRM for a jump
  // that has to be taken whole, and otherwise it is the plain black way out.
  function renderClaim(c, n) {
    var btn = $('[data-fs-claim]');
    if (!btn) return;
    var a = offerAction(c, n);
    var m = a.main, sb = a.sub;

    // This button and the escape link must never read alike (Derrick 2026-07-31: "make
    // it so the words of secure checkout and checkout now are different"). The link is
    // therefore shown ONLY while this button is a confirm; the moment it becomes
    // "Secure checkout" the link would be the same sentence twice, so it hides and
    // there is exactly one control instead of two near-identical ones.
    if (a.kind === 'idle') { m = 'Secure checkout →'; sb = ''; }
    var skipBtn = $('[data-fs-skip]');
    if (skipBtn) skipBtn.hidden = a.kind === 'idle';

    btn.disabled = a.disabled;
    btn.classList.toggle('is-waiting', a.disabled);
    var main = $('[data-fs-claim-main]');
    var sub = $('[data-fs-claim-sub]');
    if (main) main.textContent = m;
    if (sub) { sub.textContent = sb; sub.hidden = !sb; }
  }

  // THE OFFER'S PRIMARY BUTTON, in the cart drawer footer (Derrick 2026-07-31: "the
  // green button is supposed to go here"). The offer was a modal-only thing first, which
  // had it backwards: the deal was reachable only by clicking checkout and being
  // interrupted, so a shopper had to be leaving before they were allowed to choose. The
  // pitch belongs where the shopper already stands.
  //
  // The CARDS are not here — they are in the popup this button opens (Derrick 2026-08-02:
  // "i want it to be like its own pop up"). A one-day experiment put them inline in the
  // drawer and the button scrolled down to them; that made the shopper hunt, and on a
  // phone it left the drawer taller than the screen.
  //
  // Rendered ONLY from a server-confirmed ladder. No confirmation, no button: the drawer
  // stays exactly as the theme shipped it rather than advertising an offer the store may
  // no longer be running.
  function renderDrawerCta(c, n) {
    var btn = document.querySelector('[data-fs-drawer-cta]');
    var link = document.querySelector('[data-checkout-link]');
    // An offer with nothing left to pick is not an offer this button can honour.
    var show = hasOffer(n) && pickableCount(lastCart || {}) > 0;
    var a = show ? offerAction(c, n) : { kind: 'idle', main: '', sub: '', disabled: false };

    if (show && a.kind === 'idle') {
      if (c.slots > 0) {
        a.main = 'Claim my free gift →';
        a.sub = c.slots === 1 ? '1 free pair — no extra charge'
                              : c.slots + ' free pairs — no extra charge';
      } else {
        // No free slot, but a better rung within reach — the step that makes the chain
        // a chain. Framed as what it costs, because it does cost: this is a knowingly
        // better deal, not a gift, and the button must not imply otherwise.
        var nu = nextUnlock(n);
        a.main = 'Get ' + nu.addPairs + ' more ' + plural(nu.addPairs, 'pair') + ' →';
        // Describes the DEAL, not a batch. Picks land one at a time now, so "2 pairs
        // added" would have been a promise a single tap did not keep.
        a.sub = 'Pay for ' + (nu.addPaid === 1 ? 'one' : nu.addPaid) + ' — the rest come free';
      }
    }

    if (btn) {
      btn.hidden = !show;
      btn.disabled = a.disabled;
      btn.classList.toggle('is-waiting', a.disabled);
      var main = btn.querySelector('[data-fs-drawer-main]');
      var sub = btn.querySelector('[data-fs-drawer-sub]');
      if (main) main.textContent = a.main;
      if (sub) { sub.textContent = a.sub; sub.hidden = !a.sub; }
    }

    // Checkout is never removed — only out-ranked (Derrick 2026-07-31: "the cart drawer
    // should also get the checkout now option"). With the offer above it this link is
    // the quiet escape beside it; with no offer it is the footer's only control and
    // keeps the theme's default black button, untouched.
    if (link && !modal.contains(link)) {
      link.textContent = show ? 'Or checkout now →' : 'CHECKOUT';
      ['btn', 'btn-primary', 'btn-block', 'btn-lg'].forEach(function (cls) {
        link.classList.toggle(cls, !show);
      });
      link.classList.toggle('fs-skip', show);
    }
  }

  function renderBar(c) {
    var bar = document.querySelector('[data-offer-bar]');
    if (!bar) return;
    var msg = bar.querySelector('[data-offer-msg]');
    var fill = bar.querySelector('[data-offer-fill]');
    if (msg) msg.textContent = c.bar;
    if (fill) fill.style.width = Math.max(0, Math.min(100, c.progress || 0)) + '%';
  }

  function renderNudge(c, count) {
    var nudge = document.querySelector('[data-freeshoe-nudge]');
    if (!nudge) return;
    // Silent whenever the drawer's own green button is on screen. The nudge existed to
    // tell a shopper something free was waiting for them at checkout; with a button
    // saying "Claim my free gift" a few inches below, it is the same fact stated twice —
    // which is what "too much stuff in that area" was about — and its "pick at checkout"
    // wording is wrong besides, because the button opens the picker right here.
    var cta = document.querySelector('[data-fs-drawer-cta]');
    if (count < 1 || c.slots < 1 || (cta && !cta.hidden)) { nudge.hidden = true; return; }
    nudge.hidden = false;
    // Kept short so it holds ONE line on a phone (Derrick 2026-07-31). The previous
    // wording — "waiting — pick it before checkout" — wrapped to two lines in the
    // drawer and read like a paragraph instead of a badge.
    nudge.innerHTML = '<span class="fs-nudge__txt"><b>' + c.slots + ' free '
      + plural(c.slots, 'pair') + '</b> &mdash; pick at checkout</span>';
  }

  /* ------------------------------------------------------------------ *
   * Open / close
   * ------------------------------------------------------------------ */
  var lastFocus = null;
  function openModal() {
    lastFocus = document.activeElement;
    if (overlay) { overlay.hidden = false; requestAnimationFrame(function () { overlay.classList.add('is-open'); }); }
    modal.hidden = false;
    requestAnimationFrame(function () { modal.classList.add('is-open'); });
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    var c = $('[data-fs-close]');
    if (c) setTimeout(function () { c.focus(); }, 40);
    // PAINT FIRST, VERIFY SECOND. The config re-read below used to gate the first
    // render, so the popup slid up carrying whatever the last cart event had left in it
    // and only became correct a round trip later — on a phone that reads as the popup
    // being slow, which is exactly what Derrick reported. What is already on screen is
    // not a guess: `lastCart` came from the server and CFG is only ever set from a
    // server-confirmed read, so this paint is authoritative or numberless, never a hint.
    if (lastCart) render(lastCart);
    // Then re-confirm the ladder anyway. This is the moment a promise is made, so it is
    // the moment the ladder has to be current — the second render corrects the first if
    // the store changed the offer under us.
    loadCfg(true).then(function () { if (lastCart) render(lastCart); });
  }
  function closeModal() {
    modal.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    // Only give scrolling back if nothing else still wants it locked. This used to be
    // unconditional, which was harmless while the popup was only ever reached by
    // intercepting a checkout click. It is not harmless now: the popup opens FROM the
    // cart drawer, so closing it would unlock the body while the drawer is still on
    // screen — and the page scrolls away underneath an open drawer. Same guard
    // sbs-app.js uses in closeSizeGuide.
    if (!document.querySelector('.cart-drawer.open')
        && !document.querySelector('.nav-drawer.open')
        && !document.querySelector('.filter-drawer.open')) {
      document.body.classList.remove('no-scroll');
    }
    setTimeout(function () { modal.hidden = true; if (overlay) overlay.hidden = true; }, 220);
    $$('[data-fs-sizes]', modal).forEach(function (s) { s.hidden = true; });
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ------------------------------------------------------------------ *
   * Commit + retraction
   * ------------------------------------------------------------------ */
  // Repaint from a cart we ALREADY HOLD. Split out from `afterAdd` so the commit path
  // can hand over the cart its reconcile read just fetched instead of asking the server
  // for the same document a second time.
  function paint(cart) {
    if (typeof window.sbsRenderCart === 'function') window.sbsRenderCart(cart);
    else render(cart);
  }
  function afterAdd() { return getCart().then(paint); }

  function newToken() {
    return (CFG && CFG.configId ? CFG.configId : 'offer') + ':' +
      Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  }

  // Remove ONLY the units this commit added. The private `_sbs_offer` token gives
  // each commit its own cart lines, so `quantity: 0` on those keys can never take a
  // unit the shopper added themselves. Serialized — concurrent /cart/change.js calls
  // invalidate each other's line keys.
  function retract(token) {
    return getCart().then(function (cart) {
      var mine = (cart.items || []).filter(function (li) {
        return li.properties && li.properties._sbs_offer === token;
      });
      return mine.reduce(function (chain, li) {
        return chain.then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: li.key, quantity: 0 })
          });
        });
      }, Promise.resolve());
    }).catch(function () { /* best effort — the error path already reported */ });
  }

  function commit() {
    var n = shoeCount(lastCart || {});
    // A pick made while an add is in flight is NOT dropped — it waits, and the
    // in-flight commit re-runs for it on completion. This used to `return` here and
    // then `staged = []` on success, so a second tap during the ~500ms round trip
    // showed the card go green and then silently revert. Same failure the shopper
    // reports as "it didn't add".
    if (busy || !staged.length) return;

    // Refusing has to SAY so. A silent `return` here is exactly how pairs 2-4
    // disappeared: the pick was taken, the cart never changed, and nothing explained
    // why. Reachable now only if `lastCart` is stale (another tab, a failed refresh),
    // so re-sync rather than leaving a picked card that means nothing.
    if (!commitAllowed(n, staged.length)) {
      staged = [];
      say('That offer just changed — your cart has been re-checked.');
      getCart().then(render).catch(function () {});
      return;
    }

    busy = true;
    renderClaim(offerCopy(n, grantedUnits(lastCart || {})), n);
    var token = newToken();
    // Exactly what THIS commit owns. Anything staged after this line belongs to the
    // next one.
    var mine = staged.slice();
    var items = mine.map(function (s) {
      return { id: Number(s.vid), quantity: 1, properties: { _sbs_offer: token } };
    });

    // SHOW THE PAIR NOW (Derrick 2026-08-02: "the adding stuff and everything is slow").
    // The add plus its reconcile read is ~400-700ms on a phone, and until this the
    // drawer sat perfectly still the whole time — the work was in flight and nothing on
    // screen said so, which is indistinguishable from a tap that did nothing. The real
    // line replaces this the moment the cart comes back; `renderCartDrawer` rewrites the
    // whole list, so nothing has to clean up after a success.
    //
    // Deliberately NO PRICE. Whether the store zeroes this pair is the store's call and
    // it has not made it yet — printing $150.00 on a pair the shopper was just told is
    // free would be a lie, and printing $0.00 would be a promise we cannot keep.
    if (typeof window.sbsOptimisticLine === 'function') {
      mine.forEach(function (s) {
        try {
          window.sbsOptimisticLine({ title: s.title, variantTitle: s.size ? 'Size ' + s.size : '',
                                     image: s.image || '', quantity: 1 });
        } catch (e) {}
      });
    }

    // The cart the reconcile read returns, kept so the repaint below does not have to
    // fetch it again. Stays null on every path where the cart moved after that read
    // (retraction) or where the read never answered.
    var verified = null;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (r.ok) return r.json();
      return r.json().then(function (b) { throw new Error(b.description || b.message || 'Add failed'); },
                           function () { throw new Error('Add failed'); });
    }).then(function () {
      // Reconcile even on success — a 200 is not proof every unit landed.
      return getCart().then(function (cart) {
        var landed = (cart.items || []).reduce(function (a, li) {
          return a + ((li.properties && li.properties._sbs_offer === token) ? li.quantity : 0);
        }, 0);
        if (landed !== items.length) {
          return retract(token).then(function () {
            throw new Error('Only part of that went through — nothing was added. Try again.');
          });
        }
        // Drop ONLY what this commit added. `staged = []` here would wipe a pick the
        // shopper made while the request was in the air.
        staged = staged.filter(function (s) { return mine.indexOf(s) === -1; });
        // This IS the post-add cart. Hand it to the repaint below instead of asking the
        // server for the identical document a second time — that third round trip was
        // pure latency on every single pick, and on a throttled connection it was also a
        // third chance to hit the /cart* rate limit.
        verified = cart;
      }, function (readErr) {
        // COULD NOT VERIFY — which is NOT the same as "it failed".
        //
        // This rejection handler is the whole point. Without it the failed read fell
        // through to the outer .catch(), which retracts, so a single throttled /cart.js
        // DELETED a pair the store had already accepted and told the shopper "cart read
        // failed". Reversing a good add because the check didn't answer is destructive;
        // the add returned 200 and its units are in the cart.
        //
        // So: keep the pairs, clear this commit's picks, say something true, and let the
        // next render reconcile. Retraction stays reserved for a partial landing we have
        // actually SEEN.
        staged = staged.filter(function (s) { return mine.indexOf(s) === -1; });
        say('Added — refreshing your cart…');
        try {
          console.warn('[sbs-offer] could not verify the add, keeping it: '
            + (readErr && readErr.message || readErr));
        } catch (e) {}
      });
    }).catch(function (err) {
      say(err.message || 'Add to cart failed');
      staged = staged.filter(function (s) { return mine.indexOf(s) === -1; });
      // Take the optimistic line back down NOW. The repaint below normally does it by
      // rewriting the whole list, but on the one path where BOTH the add and the repaint
      // fail that repaint is 1.2s away — and a phantom pair sitting in the cart for over
      // a second after a failed add is worse than the failure.
      if (typeof window.sbsOptimisticRollback === 'function') {
        try { window.sbsOptimisticRollback(items.length); } catch (e) {}
      }
      return retract(token);
    }).then(function () {
      // Clear busy BEFORE the render that reads it, or the button renders disabled
      // and stays that way until the next cart event.
      busy = false;
      // Reuse the reconcile read when we have it; only go back to the server when the
      // cart genuinely moved since (a retraction) or when that read never answered.
      var repaint = verified ? Promise.resolve(paint(verified)) : afterAdd();
      return repaint.then(function () {
        // A pick landed mid-flight. Honour it now rather than leaving a card sitting
        // green over a cart that never received it.
        if (staged.length) commit();
      });
    // THIS CATCH IS THE POINT. `afterAdd()` re-reads /cart.js to repaint, and that read
    // can fail on its own — Shopify rate-limits /cart* per IP, so the read right after
    // a successful add is exactly where a 429 lands. With no catch it surfaced as
    // "Uncaught (in promise) Error: cart" and nothing else: the pair WAS in the cart,
    // the drawer kept showing the old state, and the shopper was told nothing. The add
    // had already succeeded, so the honest message is "it worked, the view is stale" —
    // never "it failed" — and then try the repaint once more.
    }).catch(function (err) {
      busy = false;
      say('Added — refreshing your cart…');
      try { console.warn('[sbs-offer] post-add refresh failed: ' + (err && err.message || err)); } catch (e) {}
      setTimeout(function () { afterAdd().catch(function () {}); }, 1200);
    });
  }

  /* ------------------------------------------------------------------ *
   * Interaction
   * ------------------------------------------------------------------ */
  function openSizes(card) {
    var box = card.querySelector('[data-fs-sizes]');
    var addBtn = card.querySelector('[data-fs-add]');
    if (!box) return;
    var variants = [];
    try { variants = JSON.parse(card.querySelector('[data-fs-variants]').textContent) || []; } catch (e) {}
    var seen = {}, html = '';
    variants.forEach(function (v) {
      var sz = v.s == null ? '' : String(v.s);
      if (sz === '' || seen[sz]) return;
      seen[sz] = true;
      html += '<button type="button" class="fs-size' + (v.a ? '' : ' oos') + '" data-vid="' + v.id + '"'
            + ' data-size="' + esc(sz) + '"' + (v.a ? '' : ' disabled') + '>' + esc(sz) + '</button>';
    });
    // Label the step. Without it the card silently swapped a button for a row of bare
    // numbers, and nothing said those numbers were sizes or that tapping one adds the
    // pair (Derrick, 2026-07-31: "its hard to know how to add something free").
    box.innerHTML = html
      ? '<span class="fs-size__label">Tap your size</span>' + html
      : '<span class="fs-size__label">Sold out</span>';
    box.hidden = false;
    if (addBtn) addBtn.hidden = true;
  }

  // Document-level, not modal-level: the picker renders on two surfaces now, and a
  // handler bound to the modal would leave every card in the drawer inert.
  document.addEventListener('click', function (e) {
    var closeBtn = e.target.closest('[data-fs-close], [data-fs-skip]');
    if (closeBtn) {
      e.preventDefault();
      if (closeBtn.hasAttribute('data-fs-skip')) window.location.href = '/checkout';
      else closeModal();
      return;
    }

    var claim = e.target.closest('[data-fs-claim]');
    if (claim) {
      var nNow2 = shoeCount(lastCart || {});
      // Confirm a staged jump; otherwise the button says "Secure checkout" and that is
      // exactly what it does. It used to open the first card here instead, which made a
      // control labelled as an exit behave like a picker.
      if (staged.length && commitAllowed(nNow2, staged.length)) { commit(); return; }
      window.location.href = '/checkout';
      return;
    }

    var card = e.target.closest('[data-fs-card]');

    // The WHOLE card opens the size picker, not just the button. On a phone the button
    // is a small target under a large photo, and tapping the photo — the obvious thing
    // to do — did nothing at all.
    var sizeBox = e.target.closest('[data-fs-sizes]');
    if (card && !sizeBox && !card.classList.contains('fs-card--locked')) {
      var pid = Number(card.getAttribute('data-pid'));
      if (stagedFor(pid) > 0) {           // tapping a picked card un-picks it
        staged = staged.filter(function (s) { return s.pid !== pid; });
        if (lastCart) render(lastCart);
      } else {
        openSizes(card);
      }
      return;
    }

    var size = e.target.closest('.fs-size');
    if (size && !size.disabled && card) {
      var vid = size.getAttribute('data-vid');
      if (!vid) return;
      var nNow = shoeCount(lastCart || {});
      if (staged.length >= pickLimit(nNow)) { say('That’s everything this cart can add on the offer.'); return; }
      var thumb = card.querySelector('img');
      staged.push({
        vid: Number(vid),
        pid: Number(card.getAttribute('data-pid')),
        title: (card.querySelector('.fs-card__t') || {}).textContent || '',
        size: size.getAttribute('data-size') || '',
        // Carried purely so the optimistic cart line has a picture to show while the add
        // is in flight. The card is already holding it, so this costs nothing.
        image: thumb && thumb.getAttribute ? (thumb.getAttribute('src') || '') : ''
      });
      var box = card.querySelector('[data-fs-sizes]');
      if (box) box.hidden = true;

      // ADD IMMEDIATELY. Always.
      //
      // This was gated on `freeSlots >= 1`, so a FREE pick landed on tap while a
      // next-rung pick only staged — and the control that would have committed it sits
      // in the drawer footer, which Shopify's own preview bar covers on a phone. Two
      // different behaviours behind one identical gesture, and the paid one looked
      // broken (Derrick 2026-07-31: "the extra things im adding for shoe pairs 2-4 dont
      // work and arent being added"; and before that, "the adding also didnt work").
      //
      // One gesture, one outcome: tapping a size puts the pair in the cart. Atomicity
      // is not lost — `commitAllowed` still refuses anything past the rung, and a
      // partial jump is safe on this ladder because the very next pair comes free.
      commit();
      return;
    }
  });

  if (overlay) overlay.addEventListener('click', closeModal);
  // ESCAPE CLOSES THE POPUP AND STOPS THERE.
  //
  // sbs-app.js runs a deliberate close-only-the-topmost-overlay ladder on Escape
  // (JS-H-07: size guide -> picker -> filter -> nav -> cart), written because "a buyer
  // who opened Size Guide FROM the picker lost picker state on one keystroke". That
  // ladder does not know this popup exists, so it falls through to `closeCart()`.
  //
  // That was survivable while the only way in was intercepting a checkout click — the
  // shopper was on their way out anyway. It is not survivable now that the popup opens
  // FROM an open cart drawer: one Escape took down the popup AND the drawer, which is
  // precisely the bug that ladder exists to prevent, reintroduced from above. Caught by
  // a pre-publish walk 2026-08-02.
  //
  // CAPTURE phase, because both listeners sit on `document` and bubble-phase order would
  // depend on which asset the layout happens to load first.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !modal.classList.contains('is-open')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    closeModal();
  }, true);

  // The drawer's offer button — THE WAY INTO THE POPUP.
  //
  // Derrick 2026-08-02: "when you click the get free pair button it scrolls them down to
  // the cart — i want it to be like its own pop up and they can checkout from the popup
  // and stuff too, like how it is on the current live theme." For one day this scrolled
  // the drawer to an inline rail and opened the first card's sizes, which made the
  // shopper hunt for what they had just asked for, and on a phone left the drawer taller
  // than the screen. The picker is a popup again, and this is its trigger.
  //
  // Capture phase so it wins outright; nothing else in the footer should act on it.
  document.addEventListener('click', function (e) {
    var cta = e.target.closest('[data-fs-drawer-cta]');
    if (!cta || cta.hidden || cta.disabled) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var n = shoeCount(lastCart || {});
    // Still the CONFIRM when a jump is staged. Size taps commit on their own now, so
    // this is a fallback path rather than the usual one, but a button that reads
    // "Add 2 pairs →" has to do that and not re-open the popup over the top of it.
    if (staged.length && commitAllowed(n, staged.length)) { commit(); return; }
    if (pickableCount(lastCart || {}) < 1) {
      // Should be unreachable — the button hides when nothing is pickable — but a
      // control that does nothing and says nothing is the bug this whole sweep is about.
      say('Nothing left to pick on this offer right now.');
      return;
    }
    openModal();
  }, true);

  // Checkout intercept — capture phase, ahead of sbs-app's stale-cart guard.
  // Only interrupts when the ladder is CONFIRMED and there is genuinely something
  // free waiting. An unverified offer must never stand between a shopper and
  // checkout: a lost order costs more than a missed upsell.
  document.addEventListener('click', function (e) {
    var link = e.target.closest('[data-checkout-link]');
    if (!link || modal.contains(link)) return;
    if (!ready()) return;

    // Stand down when the drawer's offer button is ON SCREEN — which means the drawer is
    // OPEN, not merely present. The shopper is looking straight at "Claim my free gift"
    // and this link is the explicit "or checkout now" underneath it; interrupting there
    // would turn the escape hatch into a trap. They can still reach the popup any time —
    // it is one tap on the button right above this link.
    //
    // `!cta.hidden` alone was wrong. The drawer markup is on every page, so its button
    // reports "not hidden" whenever an offer stands even while the drawer is shut —
    // which stood the intercept down on the CART PAGE, where there is no picker at all.
    // That page showed an offer bar promising "add 1 more pair, it's free" and gave the
    // shopper no way whatsoever to take it. The modal exists precisely for a checkout
    // path that has not already shown the offer; this is that path.
    var cta = document.querySelector('[data-fs-drawer-cta]');
    var drawer = document.querySelector('[data-cart-drawer]');
    var drawerOpen = !!drawer && drawer.classList.contains('open');
    if (cta && !cta.hidden && drawerOpen) return;

    var n = shoeCount(lastCart || {});

    // Interrupt when there is EITHER a free pair waiting OR a better rung within reach.
    // This used to require a free slot, so a 2-pair cart — which has no free slot but
    // does have "get 2 more pairs, pay for the one you just added" — sailed straight
    // through to checkout with the offer never shown (Derrick, 2026-07-31: "the second
    // upsell flow didnt happen ... i just went to checkout"). The whole point of the
    // chain is the step AFTER the first free pair.
    if (freeSlots(n) < 1 && !nextUnlock(n)) return;
    // No once-per-cart guard (Derrick 2026-07-31: "they exit out, click checkout again,
    // it takes them straight to checkout"). A guard meant a shopper who closed the modal
    // to think about it could never get back to the offer. The re-show is not a trap
    // now for a stronger reason than the escape link: the offer lives permanently in
    // the drawer footer, so it is never something a shopper has to be interrupted to
    // reach in the first place.

    e.preventDefault();
    e.stopImmediatePropagation();
    openModal();
  }, true);

  document.addEventListener('sbs:cart:updated', function (e) {
    if (e.detail) render(e.detail);
    // RE-ARM after a transient config failure. Found by a browser walk 2026-08-02: the
    // page happened to load while this IP was rate-limited, the boot `/cart?view=offer`
    // read came back 429, and the offer then stayed dead for the ENTIRE page view — the
    // green button hides when the ladder is unconfirmed, and the only other caller of
    // loadCfg is openModal(), which is behind that same hidden button. One blip and the
    // whole chain is unreachable until a reload.
    //
    // Failing closed on an unconfirmed ladder is right and stays. Failing closed FOREVER
    // is not. A cart mutation is a user action, so retrying here is naturally bounded —
    // no polling — and loadCfg already collapses concurrent calls onto one request.
    if (!CFG_AUTHORITATIVE) {
      loadCfg(true).then(function (cfg) { if (cfg && lastCart) render(lastCart); });
    }
  });

  /* Read-only view of the ladder, for verification and for debugging a live cart
     from the console. Deliberately has NO setter: the only way to change the ladder
     is the shop metafield, so a debug hook that could inject one would be a second
     source of truth. tests/test_sbs_offer_ladder_2026_07_31.py drives this through
     the real config fetch. */
  window.sbsOfferLadder = {
    ready: ready,
    config: function () { return CFG && { rungs: CFG.rungs, measuredThrough: CFG.measuredThrough, configId: CFG.configId, revision: CFG.revision }; },
    freeAt: freeAt,
    paidAt: paidAt,
    freeSlots: freeSlots,
    commitOk: commitOk,
    commitAllowed: commitAllowed,
    pickLimit: pickLimit,
    nextUnlock: nextUnlock,
    copy: offerCopy,
    granted: grantedUnits,
    count: shoeCount
  };

  // Boot: confirm the ladder from the server, then paint. Until it returns, the
  // server-rendered numberless copy stands.
  Promise.all([loadCfg(true), getCart().catch(function () { return null; })])
    .then(function (r) { if (r[1]) render(r[1]); });
})();
